import { mediaQueryLarge, isMobileBreakpoint } from '@theme/utilities';

// Accordion
// Still extends HTMLElement over Component so that refs are still available to parent components (e.g. SortingFilterComponent)
class AccordionCustom extends HTMLElement {
  /** @type {HTMLDetailsElement} */
  get details() {
    const details = this.querySelector('details');

    if (!(details instanceof HTMLDetailsElement)) throw new Error('Details element not found');

    return details;
  }

  /** @type {HTMLElement} */
  get summary() {
    const summary = this.details.querySelector('summary');

    if (!(summary instanceof HTMLElement)) throw new Error('Summary element not found');

    return summary;
  }

  get mobileDialog() {
    const dialog = this.querySelector('.mv-product-accordion-dialog');

    return dialog instanceof HTMLDialogElement ? dialog : null;
  }

  get #disableOnMobile() {
    return this.dataset.disableOnMobile === 'true';
  }

  get #disableOnDesktop() {
    return this.dataset.disableOnDesktop === 'true';
  }

  get #closeWithEscape() {
    return this.dataset.closeWithEscape === 'true';
  }

  #controller = new AbortController();
  #contentPlaceholder = null;

  connectedCallback() {
    if (this.#controller.signal.aborted) this.#controller = new AbortController();
    const { signal } = this.#controller;

    this.#setDefaultOpenState();

    this.addEventListener('keydown', this.#handleKeyDown, { signal });
    this.summary.addEventListener('click', this.handleClick, { signal });
    mediaQueryLarge.addEventListener('change', this.#handleMediaQueryChange, { signal });

    const dialog = this.mobileDialog;
    if (dialog) {
      dialog.querySelector('[data-mobile-accordion-close]')?.addEventListener('click', () => dialog.close(), { signal });
      dialog.addEventListener('close', this.#handleMobileDialogClose, { signal });
      dialog.addEventListener('click', this.#handleMobileDialogClick, { signal });
    }
  }

  /**
   * Handles the disconnect event.
   */
  disconnectedCallback() {
    if (this.mobileDialog?.open) this.mobileDialog.close();
    this.#restoreMobileContent();
    // Disconnect all the event listeners
    this.#controller.abort();
  }

  /**
   * Handles the click event.
   * @param {Event} event - The event.
   */
  handleClick = (event) => {
    const isMobile = isMobileBreakpoint();
    const isDesktop = !isMobile;

    // Stop default behaviour from the browser
    if ((isMobile && this.#disableOnMobile) || (isDesktop && this.#disableOnDesktop)) {
      event.preventDefault();
      return;
    }

    if (isMobile && this.mobileDialog) {
      event.preventDefault();
      this.#openMobileDialog();
    }
  };

  #openMobileDialog() {
    const dialog = this.mobileDialog;
    const content = this.details.querySelector('.details-content');
    const body = dialog?.querySelector('.mv-product-accordion-dialog__body');

    if (!dialog || !content || !body || dialog.open) return;

    this.details.open = false;
    this.#contentPlaceholder = document.createComment('accordion content');
    content.before(this.#contentPlaceholder);
    body.append(content);
    dialog.showModal();
    dialog.querySelector('[data-mobile-accordion-close]')?.focus();
  }

  #handleMobileDialogClick = (event) => {
    if (event.target === this.mobileDialog) this.mobileDialog.close();
  };

  #handleMobileDialogClose = () => {
    this.#restoreMobileContent();
    if (this.isConnected && isMobileBreakpoint()) this.summary.focus();
  };

  #restoreMobileContent() {
    const content = this.mobileDialog?.querySelector('.details-content');
    if (content && this.#contentPlaceholder?.parentNode) this.#contentPlaceholder.replaceWith(content);
    this.#contentPlaceholder = null;
  }

  /**
   * Handles the media query change event.
   */
  #handleMediaQueryChange = () => {
    if (this.mobileDialog?.open) this.mobileDialog.close();
    this.#setDefaultOpenState();
  };

  /**
   * Sets the default open state of the accordion based on the `open-by-default-on-mobile` and `open-by-default-on-desktop` attributes.
   */
  #setDefaultOpenState() {
    const isMobile = isMobileBreakpoint();

    if (isMobile && this.mobileDialog) {
      this.details.open = false;
      return;
    }

    this.details.open =
      (isMobile && this.hasAttribute('open-by-default-on-mobile')) ||
      (!isMobile && this.hasAttribute('open-by-default-on-desktop'));
  }

  /**
   * Handles keydown events for the accordion
   *
   * @param {KeyboardEvent} event - The keyboard event.
   */
  #handleKeyDown(event) {
    // Close the accordion when used as a menu
    if (event.key === 'Escape' && this.#closeWithEscape) {
      event.preventDefault();

      this.details.open = false;
      this.summary.focus();
    }
  }
}

if (!customElements.get('accordion-custom')) {
  customElements.define('accordion-custom', AccordionCustom);
}
